import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'

import {
  SelectInput,
  DecimalInput,
  isNumber,
} from '../Inputs/Inputs'

import RedX from '../Graphics/RedX'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'

function valueKeyToSensor (valueKey) {
  let sensor
  switch (valueKey) {
    case '128':
    case 'VTF':
      sensor = 'tempF'
      break
    case '129':
    case 'IB':
      sensor = 'batteryInternal'
      break
    case '130':
      sensor = 'batteryExternal'
      break
    case '131':
      sensor = 'engineState'
      break
    case '134':
      sensor = 'rpm'
      break
    default:
      sensor = 'tempF'
      break
  }
  return sensor
}

function General ({ intl, ...props }) {
  const [state, setState] = useState({
    test: props.test,
    formula: props.formula,
    valueKey: props.valueKey,
    testValue: props.testValue,
    multiplier: props.multiplier,
    body: props.body,
    subject: props.subject,
    debounceTime: props.debounceTime,
    sendOnce: props.sendOnce,
    notificationInterval: props.notificationInterval,
    type: props.type,
    sensor: valueKeyToSensor(props.valueKey),
    invalid: false,
    testValueValidation: props.testValue,
  })

  const updateNewRule = useCallback(() => {
    AppDispatcher.dispatch({
      action: 'update-new-rule-parameters',
      body: state.body,
      debounceTime: state.debounceTime,
      formula: state.formula,
      invalid: state.invalid,
      multiplier: state.multiplier,
      notificationInterval: state.notificationInterval,
      sendOnce: state.sendOnce,
      sensor: state.sensor,
      subject: state.subject,
      template: 'general',
      test: state.test,
      testValue: state.testValue,
      type: state.type,
      valueKey: state.valueKey,
    })
  }, [state])

  useEffect(() => {
    updateNewRule()
  }, [updateNewRule])

  const handleChangeInterval = useCallback((field, value, callback) => {
    setState((prevState) => ({
      ...prevState,
      [field]: Number(value),
    }))

    if (callback) setTimeout(callback, 0)
  }, [])

  const handleChangeSensor = useCallback((field, value, callback) => {
    const { formatMessage } = intl

    let formula
    let valueKey
    let body
    let multiplier
    switch (value) {
      case 'tempF':
        formula = 'temperatureF'
        valueKey = '128'
        body = formatMessage({
          id: 'rules.temperature.ambient.message',
          defaultMessage: 'Temperature at device \\{\\{deviceNumber\\}\\} is \\{\\{128,{temperature},1\\}\\} {unit}',
          description: 'Temperature rule',
        }, {
          temperature: formula,
          unit: 'f',
        })
        multiplier = 1
        break
      case 'batteryInternal':
        formula = 'default'
        valueKey = '129'
        body = formatMessage({
          id: 'rules.batteryInternal.message',
          defaultMessage: 'Internal Battery at device \\{\\{deviceNumber\\}\\} is \\{\\{129,default,0.01\\}\\}%.',
          description: 'Internal battery at device message',
        }, {
          none: null,
        })
        multiplier = 0.01
        break
      case 'batteryExternal':
        formula = 'percentTo20V'
        valueKey = '130'
        body = formatMessage({
          id: 'rules.batteryExternal.message',
          defaultMessage: 'Battery Voltage at device \\{\\{deviceNumber\\}\\} is \\{\\{130,percentTo20V,1\\}\\} volts.',
          description: 'Battery external at device message',
        }, {
          none: null,
        })
        multiplier = 1
        break
      case 'engineState':
        formula = 'default'
        valueKey = '131'
        body = formatMessage({
          id: 'rules.machineState.message',
          defaultMessage: 'Machine \\{\\{deviceNumber\\}\\} is \\{\\{131,valveStatus,1\\}\\}',
          description: 'Machine state at device message',
        }, {
          none: null,
        })
        multiplier = 1
        break
      case 'rpm':
        formula = 'default'
        valueKey = '134'
        body = formatMessage({
          id: 'rules.speed.message',
          defaultMessage: 'Machine \\{\\{deviceNumber\\}\\} Speed is \\{\\{134,default,1\\}\\}',
          description: 'Machine Speed at device message',
        }, {
          none: null,
        })
        multiplier = 1
        break
      default:
        formula = 'temperatureF'
        valueKey = '128'
        body = formatMessage({
          id: 'rules.temperature.ambient.message',
          defaultMessage: 'Temperature at device \\{\\{deviceNumber\\}\\} is \\{\\{128,{temperature},1\\}\\} {unit}',
          description: 'Temperature rule',
        }, {
          temperature: formula,
          unit: 'f',
        })
        multiplier = 1
        break
    }

    setState((prevState) => ({
      ...prevState,
      formula,
      valueKey,
      multiplier,
      body,
      sensor: value,
    }))

    if (callback) setTimeout(callback, 0)
  }, [intl])

  const handleChange = useCallback((field, value, callback) => {
    let valueU = value
    if (field === 'sendOnce') {
      valueU = value === 'true'
    }

    setState((prevState) => ({
      ...prevState,
      [field]: valueU,
    }))

    if (callback) setTimeout(callback, 0)
  }, [])

  const handleChangeDecimal = useCallback((field, value, callback) => {
    const valueNumber = Number(value)
    const valueCorrect = value !== '' && isNumber(valueNumber)

    if (valueCorrect) {
      setState((prevState) => ({
        ...prevState,
        [field]: valueNumber,
        invalid: !valueCorrect,
        testValueValidation: value,
      }))
    } else {
      setState((prevState) => ({
        ...prevState,
        invalid: !valueCorrect,
        testValueValidation: value,
      }))
    }

    if (callback) setTimeout(callback, 0)
  }, [])

  const childChange = useCallback((event) => {
    switch (event.target.name) {
      case 'sensor':
        handleChangeSensor(event.target.name, event.target.value)
        break
      case 'testValue':
        handleChangeDecimal(event.target.name, event.target.value)
        break
      case 'test':
      case 'valueKey':
      case 'method':
      case 'sendOnce':
        handleChange(event.target.name, event.target.value)
        break
      case 'notificationInterval':
        handleChangeInterval(event.target.name, event.target.value)
        break
      default:
        break
    }
  }, [handleChangeSensor, handleChangeDecimal, handleChange, handleChangeInterval])

  const { formatMessage } = intl

  const sensorOptions = [
    {
      value: 'tempF',
      name: formatMessage({
        id: 'sensor.temperature.ambient',
        defaultMessage: 'Ambient Temperature',
        description: 'Ambient Temperature option',
      }),
    },
    {
      value: 'batteryInternal',
      name: formatMessage({
        id: 'sensor.battery.internal',
        defaultMessage: 'Internal Battery',
        description: 'Internal Battery option',
      }),
    },
    {
      value: 'batteryExternal',
      name: formatMessage({
        id: 'sensor.battery.external',
        defaultMessage: 'Battery Voltage',
        description: 'Battery Voltage option',
      }),
    },
    {
      value: 'engineState',
      name: formatMessage({
        id: 'sensor.engine.state',
        defaultMessage: 'Engine State',
        description: 'Engine State option',
      }),
    },
    {
      value: 'rpm',
      name: formatMessage({
        id: 'sensor.rpm',
        defaultMessage: 'RPM',
        description: 'RPM option',
      }),
    },
  ]

  let testDisplay = 'less than'

  switch (state.test) {
    case 'averageLessThan':
      testDisplay = formatMessage({
        id: 'rules.general.averageIsLessThan',
        defaultMessage: 'average is less than',
        description: 'average is less than label',
      })
      break

    case 'averageGreaterThan':
      testDisplay = formatMessage({
        id: 'rules.general.averageIsGreaterThan',
        defaultMessage: 'average is greater than',
        description: 'average is greater than label',
      })
      break

    case 'greaterThan':
      testDisplay = formatMessage({
        id: 'rules.general.isGreaterThan',
        defaultMessage: 'is greater than',
        description: 'isGreaterThan text',
      })
      break

    case 'equalTo':
      testDisplay = formatMessage({
        id: 'rules.general.isEqualTo',
        defaultMessage: 'is equal to',
        description: 'isEqualTo text',
      })
      break

    case 'lessThan':
    default:
      testDisplay = formatMessage({
        id: 'rules.general.isLessThan',
        defaultMessage: 'is less than',
        description: 'isLessThan text',
      })
      break
  }

  const sensorLabel = sensorOptions.find(opt => opt.value === state.sensor)?.name || state.sensor

  return (
    <div>
      <section>
        <FormattedMessage
          id='rules.sensors'
          defaultMessage='Sensors'
          description='Sensors heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={state.sensor}
            field='sensor'
            childChange={childChange}
            options={sensorOptions}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
      <section>
        <FormattedMessage
          id='rules.comparison'
          defaultMessage='Comparison'
          description='Comparison heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={state.test}
            field='test'
            childChange={childChange}
            options={[
              {
                value: 'greaterThan',
                name: formatMessage({
                  id: 'rules.testType.greaterThan',
                  defaultMessage: 'Greater Than',
                  description: 'Greater Than option',
                }),
              },
              {
                value: 'lessThan',
                name: formatMessage({
                  id: 'rules.testType.lessThan',
                  defaultMessage: 'Less Than',
                  description: 'Less Than option',
                }),
              },
              {
                value: 'equalTo',
                name: formatMessage({
                  id: 'rules.testType.equalTo',
                  defaultMessage: 'Equal To',
                  description: 'Equal To option',
                }),
              },
              {
                value: 'averageGreaterThan',
                name: formatMessage({
                  id: 'rules.testType.averageGreaterThan',
                  defaultMessage: 'Average Greater Than',
                  description: 'Average Greater Than option',
                }),
              },
              {
                value: 'averageLessThan',
                name: formatMessage({
                  id: 'rules.testType.averageLessThan',
                  defaultMessage: 'Average Less Than',
                  description: 'Average Less Than option',
                }),
              },
            ]}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
      <section>
        <FormattedMessage
          id='rules.alertWhen'
          defaultMessage='Alert me when: {sensor} {testDisplay}'
          description='Alert when heading'
          tagName='h5'
          values={{
            sensor: sensorLabel,
            testDisplay,
          }}
        />
        <div className={styles.ruleDecimal} style={{ position: 'relative' }}>
          <DecimalInput
            value={state.testValueValidation}
            childChange={childChange}
            field='testValue'
            style={styles.ruleDecimalInput}
          />
          {state.invalid && (
            <RedX
              style={{
                position: 'absolute',
                bottom: '8px',
                right: '16px',
                height: '24px',
                width: '24px',
              }}
            />
          )}
        </div>
      </section>
      <section>
        <FormattedMessage
          id='rules.snooze.name'
          defaultMessage='Snooze Alerts'
          description='Snooze Alerts heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={state.notificationInterval}
            field='notificationInterval'
            childChange={childChange}
            options={[
              {
                value: 0,
                name: formatMessage({
                  id: 'rules.reminders.never',
                  defaultMessage: 'None',
                  description: 'None option',
                }),
              },
              {
                value: 900000,
                name: formatMessage({
                  id: 'rules.reminders.every15Min',
                  defaultMessage: '15 minutes',
                  description: '15 minutes option',
                }),
              },
              {
                value: 3600000,
                name: formatMessage({
                  id: 'rules.reminders.every1Hrs',
                  defaultMessage: '1 hour',
                  description: '1 hour option',
                }),
              },
              {
                value: 14400000,
                name: formatMessage({
                  id: 'rules.reminders.every4Hrs',
                  defaultMessage: '4 hours',
                  description: '4 hours option',
                }),
              },
              {
                value: 64800000,
                name: formatMessage({
                  id: 'rules.reminders.every18Hrs',
                  defaultMessage: '18 hours',
                  description: '18 hours option',
                }),
              },
              {
                value: 86400000,
                name: formatMessage({
                  id: 'rules.reminders.every24Hrs',
                  defaultMessage: '24 hours',
                  description: '24 hours option',
                }),
              },
            ]}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
      <section>
        <FormattedMessage
          id='rules.reminders.name'
          defaultMessage='Send Reminders'
          description='Send Reminders heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={state.sendOnce}
            field='sendOnce'
            childChange={childChange}
            options={[
              {
                value: true,
                name: formatMessage({
                  id: 'rules.sendOnce.off',
                  defaultMessage: 'Off',
                  description: 'Off option',
                }),
              },
              {
                value: false,
                name: formatMessage({
                  id: 'rules.sendOnce.on',
                  defaultMessage: 'On',
                  description: 'On option',
                }),
              },
            ]}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
    </div>
  )
}

General.propTypes = {
  rule: PropTypes.object,
  intl: PropTypes.object.isRequired,
  test: PropTypes.any,
  formula: PropTypes.string,
  valueKey: PropTypes.string,
  testValue: PropTypes.any,
  multiplier: PropTypes.number,
  body: PropTypes.string,
  subject: PropTypes.string,
  debounceTime: PropTypes.number,
  sendOnce: PropTypes.bool,
  notificationInterval: PropTypes.number,
  type: PropTypes.string,
}

export default injectIntl(General)
